/**
 * 智能体任务 API (直接调用 happy-server)
 *
 * 简化版架构：
 * im-web → happy-server → happy-cli (WebSocket RPC)
 *
 * 进度回调通过 IM WebSocket 推送:
 * happy-server → im-platform → im-web
 */
import axios from 'axios'

console.log('[AgentTask API] 🚀 agentTaskApi.js 模块已加载')
console.log('[AgentTask API] Happy Server URL:', process.env.VUE_APP_HAPPY_SERVER_URL)

// 创建 Happy Server API axios实例
const happyHttp = axios.create({
	baseURL: process.env.VUE_APP_HAPPY_SERVER_URL || 'https://happy.service.thinkgs.cn',
	timeout: 1000 * 60 * 2, // 2分钟超时
	withCredentials: false
})

// 请求拦截器 - 使用 Happy token 认证
happyHttp.interceptors.request.use(config => {
	// 使用 Happy token (不是 IM token)
	const happyToken = sessionStorage.getItem('happyToken') || localStorage.getItem('happyToken')
	console.log('[AgentTask API] 📤 发送请求:', {
		url: config.baseURL + config.url,
		method: config.method,
		hasToken: !!happyToken
	})
	if (happyToken) {
		config.headers.Authorization = `Bearer ${happyToken}`
	}
	return config
}, error => {
	console.error('[AgentTask API] ❌ 请求拦截器错误:', error)
	return Promise.reject(error)
})

// 响应拦截器
happyHttp.interceptors.response.use(response => {
	console.log('[AgentTask API] ✅ 收到响应:', {
		url: response.config.url,
		status: response.status,
		data: response.data
	})
	return response.data
}, error => {
	console.error('[AgentTask API] ❌ 请求失败:', error)
	console.error('[AgentTask API] ❌ 错误详情:', {
		url: error.config?.url,
		status: error.response?.status,
		message: error.message
	})
	if (error.response) {
		const message = error.response.data?.message || error.response.data?.error || error.message
		return Promise.reject(new Error(message))
	} else if (error.request) {
		return Promise.reject(new Error('Happy Server 服务无响应'))
	} else {
		return Promise.reject(error)
	}
})

/**
 * 执行智能体任务 (通过 happy-server 直接调用)
 *
 * 流程：
 * 1. 调用 happy-server POST /v1/tasks/execute
 * 2. happy-server 通过 RPC 调用 happy-cli 创建会话
 * 3. 进度/结果通过 IM WebSocket 推送到前端
 *
 * @param {Object} params - 任务参数
 * @param {string} params.agentId - 智能体ID
 * @param {string} params.agentName - 智能体名称
 * @param {string} params.taskContent - 任务内容
 * @param {Object} params.imContext - IM上下文
 * @param {number} params.imContext.userId - IM用户ID
 * @param {string} params.imContext.sessionId - IM会话ID
 * @param {string} params.imContext.sessionType - 会话类型 (PRIVATE/GROUP)
 * @param {Array} params.quotedMessages - 引用的IM消息列表（可选）
 * @returns {Promise<Object>} 执行结果 { success, taskId, sessionId }
 */
export async function executeAgentTask(params) {
	const { agentId, agentName, taskContent, imContext, quotedMessages } = params

	console.log('[AgentTask API] 🤖 开始执行智能体任务:', {
		agentId,
		agentName,
		taskContentPreview: taskContent?.substring(0, 50) + '...',
		quotedMessagesCount: quotedMessages?.length || 0
	})

	try {
		// 构造 machineId (格式: vnc-{userId})
		const machineId = `vnc-${imContext.userId}`

		// 构造回调 URL
		const callbackUrl = `${process.env.VUE_APP_BASE_API}/agent/task/callback`

		const result = await happyHttp({
			url: '/v1/tasks/execute',
			method: 'POST',
			data: {
				machineId,
				agentId,
				agentName,
				taskContent,
				callbackUrl,
				imContext: {
					userId: imContext.userId,
					sessionId: imContext.sessionId,
					sessionType: imContext.sessionType,
					tokenKey: imContext.tokenKey
				},
				quotedMessages: quotedMessages || []
			}
		})

		console.log('[AgentTask API] ✅ 智能体任务已启动:', result)

		return {
			success: true,
			taskId: result.taskId,
			sessionId: result.sessionId,
			shareUrl: result.shareUrl,
			message: '任务已启动'
		}

	} catch (error) {
		console.error('[AgentTask API] ❌ 智能体任务执行失败:', error)
		return {
			success: false,
			error: error.message || '任务执行失败'
		}
	}
}

/**
 * 设置 Happy token
 * @param {string} token - Happy token
 */
export function setHappyToken(token) {
	sessionStorage.setItem('happyToken', token)
	localStorage.setItem('happyToken', token)
}

/**
 * 获取 Happy token
 * @returns {string|null}
 */
export function getHappyToken() {
	return sessionStorage.getItem('happyToken') || localStorage.getItem('happyToken')
}

/**
 * 清除 Happy token
 */
export function clearHappyToken() {
	sessionStorage.removeItem('happyToken')
	localStorage.removeItem('happyToken')
}

// 消息类型常量 (与 im-platform MessageType 对应)
export const AgentTaskMessageType = {
	PROGRESS: 60,  // 智能体任务进度
	RESULT: 61,    // 智能体任务结果
	ERROR: 62      // 智能体任务错误
}

/**
 * 判断是否为智能体任务消息
 * @param {number} type - 消息类型
 * @returns {boolean}
 */
export function isAgentTaskMessage(type) {
	return type >= 60 && type < 70
}

export default {
	executeAgentTask,
	setHappyToken,
	getHappyToken,
	clearHappyToken,
	AgentTaskMessageType,
	isAgentTaskMessage
}
